import { getSiteUrl } from '../utils/siteConfig';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

/**
 * Breadcrumbs component with schema.org structured data
 * Improves SEO and navigation UX
 */
export function Breadcrumbs({ items }: BreadcrumbsProps) {
  const siteUrl = getSiteUrl();

  // Generate schema.org BreadcrumbList structured data
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.href && { item: `${siteUrl}${item.href}` }),
    })),
  };

  return (
    <>
      {/* Inject structured data */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Breadcrumb navigation */}
      <nav
        aria-label='Breadcrumb'
        className='flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 px-4 sm:px-6 lg:px-8 py-3 max-w-7xl mx-auto'
      >
        {items.map((item, index) => (
          <div key={index} className='flex items-center'>
            {index > 0 && (
              <svg
                className='w-4 h-4 mx-2 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            )}
            {item.href && index < items.length - 1 ? (
              <a href={item.href} className='hover:text-primary transition-colors'>
                {item.label}
              </a>
            ) : (
              <span
                className={
                  index === items.length - 1 ? 'font-medium text-gray-900 dark:text-white' : ''
                }
              >
                {item.label}
              </span>
            )}
          </div>
        ))}
      </nav>
    </>
  );
}
